import {useEffect, useRef} from "react";
import {router, useNavigationContainerRef} from "expo-router";
import {useAuth} from "@/auth/useAuth";
import {addNotificationTapListener, getInitialNotificationTap} from "@/lib/notification-response";
import {NOTIFICATION_INBOX_ROUTE, resolveNotificationRoute} from "@/lib/notification-router";
import {setPendingRoute} from "@/lib/pending-route";

/**
 * Renders nothing. Turns a tap on a push notification into a navigation.
 *
 * Mounted in `app/_layout.tsx`, ABOVE the auth gate, because a tap has to be heard whether or not
 * anybody is logged in — the interesting case is precisely the one where nobody is. Being above
 * the gate is also why it cannot always navigate itself: `<Authenticated>` answers an
 * unauthenticated render with `<Redirect href="/login"/>`, which would replace anything pushed
 * from here. So it either goes now or it stashes, and `<PendingRouteHandler/>` — which only exists
 * on the far side of the gate — finishes the job.
 *
 * The three cases in the plan land as two code paths, which is the point:
 *   - warm, logged in: authenticated and the navigator is up, so navigate;
 *   - cold, logged in: the launching tap is read before the auth store has finished restoring, so
 *     `isAuthenticated` is still false and it stashes — and is replayed a moment later, which is
 *     the same mechanism as the third case and needs no code of its own;
 *   - cold, logged out: stash, and the replay happens after login.
 */
export function NotificationTapHandler() {
    const {isAuthenticated} = useAuth();
    // Not a hook that re-renders: expo-router's `useNavigationContainerRef` hands back the very
    // ref its own `assertIsReady` consults, so `.isReady()` can be asked at tap time.
    const navigationRef = useNavigationContainerRef();

    // The listener and the cold-start lookup both fire outside React's control and must read the
    // CURRENT auth state, not the one captured when they were installed. A ref does that while
    // keeping ONE subscription for the life of the app; re-subscribing on every auth change would
    // open a window in which a tap is heard by nobody.
    const isAuthenticatedRef = useRef(isAuthenticated);
    useEffect(() => {
        isAuthenticatedRef.current = isAuthenticated;
    }, [isAuthenticated]);

    // A tap can be reported twice: `getInitialNotificationTap` returns the response that launched
    // the app, and on some platforms the listener reports that same response immediately after.
    // Keyed by the notification's identifier so the deep link fires once. In a ref rather than in
    // the effect so that a double-invoked effect cannot forget what it already handled.
    const handledTapIds = useRef(new Set<string>());

    useEffect(() => {
        const handle = (tap: {id: string; data: unknown}) => {
            if (handledTapIds.current.has(tap.id)) {
                return;
            }
            handledTapIds.current.add(tap.id);

            // An unroutable payload still owes the user a destination — they tapped something.
            const href = resolveNotificationRoute(tap.data) ?? NOTIFICATION_INBOX_ROUTE;

            // `router.push` is not forgiving about this: expo-router THROWS
            // ("Attempted to navigate before mounting the Root Layout component") when the root
            // navigator has not mounted. Stashing instead is both safe and correct, because every
            // way of not being able to navigate now ends with <PendingRouteHandler/> mounting.
            if (!isAuthenticatedRef.current || !navigationRef.isReady()) {
                setPendingRoute(href);
                return;
            }

            router.push(href);
        };

        // A listener alone misses the tap that started the app: it is delivered before any JS of
        // ours is running, so it has to be asked for rather than waited for.
        let disposed = false;
        void getInitialNotificationTap().then((tap) => {
            if (!disposed && tap) {
                handle(tap);
            }
        });

        const unsubscribe = addNotificationTapListener(handle);
        return () => {
            disposed = true;
            unsubscribe();
        };
    }, [navigationRef]);

    return null;
}
